/** Endpoints scénarios de travaux. */

import { apiFetch } from "@/api/client";
import type { DPEResult } from "@/api/dpe";

export type ScenarioAids = {
  maprimerenov_eur: number;
  cee_eur: number;
  eligible_eco_ptz: boolean;
  total_eur: number;
  notes: string[];
};

export type ImprovementBreakdown = {
  key: string;
  label: string;
  cost_min_eur: number;
  cost_max_eur: number;
  aids_eur: number;
  label_before: string;
  label_after: string;
  final_kwh_before: number;
  final_kwh_after: number;
  final_kwh_saved: number;
  annual_eur_saved: number;
  primary_per_m2_before: number;
  primary_per_m2_after: number;
  ges_per_m2_before: number;
  ges_per_m2_after: number;
  // Confort d'été — score 0-100 et classe (bon/moyen/insuffisant)
  summer_score_before?: number;
  summer_score_after?: number;
  summer_classe_before?: string;
  summer_classe_after?: string;
  summer_score_delta?: number;
};

export type ScenarioAmpleur = {
  is_eligible: boolean;
  class_jumps: number;
  envelope_gestures: number;
  rate_pct: number;
  ceiling_eur: number;
  mpr_eur: number;
  total_aid_ceiling_pct: number;
  accompagnateur_cost_eur: number;
  accompagnateur_aid_eur: number;
  notes: string[];
};

export type Scenario = {
  id: string;
  dpe_id: string;
  name: string;
  improvements: string[];
  result_after: DPEResult;
  cost_estimate_min: number | null;
  cost_estimate_max: number | null;
  estimated_aids: ScenarioAids | null;
  roi_years: number | null;
  created_at: string;
  updated_at: string;
  // Détail enrichi V2 (F-TRAV-003)
  breakdown?: ImprovementBreakdown[] | null;
  label_before?: string | null;
  label_after?: string | null;
  final_kwh_before?: number | null;
  final_kwh_after?: number | null;
  annual_eur_saved?: number | null;
  // Tier MaPrimeRénov' + rénovation d'ampleur (V3 - F-TRAV-005)
  tier?: "bleu" | "jaune" | "violet" | "rose" | null;
  tier_details?: {
    source?: "computed" | "default";
    reason?: string;
    is_idf?: boolean;
    household_size?: number;
    tax_reference_income_eur?: number;
    ceilings?: { bleu: number; jaune: number; violet: number };
  } | null;
  ampleur?: ScenarioAmpleur | null;
  // V3.1 — Détection obsolescence + pertinence
  engine_version?: string | null;
  current_engine_version?: string | null;
  relevance?: "excellent" | "bon" | "ok" | "marginal" | "non_rentable" | "inadapte" | null;
  relevance_message?: string | null;
  // V4 — Alertes contre-performances (guide DPE Annexe 2)
  risk_alerts?: {
    severity: "warning" | "info";
    code: string;
    title: string;
    description: string;
  }[] | null;
  // V4 — Améliorations ignorées car conflictuelles (ex : 2 systèmes de
  // chauffage cochés). On affiche un message UI explicite.
  dropped_improvements?: string[] | null;
  // Raison détaillée par clé : "conflict_with:<other_key>" ou "already_optimal".
  dropped_improvements_reasons?: Record<string, string> | null;
};

export type CreateScenarioPayload = {
  name: string;
  bouquet?: string;
  improvements?: string[];
};

export function listScenarios(logementId: string) {
  return apiFetch<Scenario[]>(`/logements/${logementId}/scenarios`);
}

/** Pertinence d'une amélioration pour le logement courant. */
export type ImprovementApplicability = {
  applicable: boolean;
  kwh_saved: number;     // gain en kWh final/an (peut être négatif)
  primary_gain: number;  // gain en kWh ep/m²/an
  // V4 — Confort d'été (Guide DPE v3 p. 29-30) : permet de classer les
  // améliorations par leur impact sur le score officiel de confort estival.
  summer_score_before?: number;
  summer_score_after?: number;
  summer_score_delta?: number;
  summer_classe_before?: string;
  summer_classe_after?: string;
  // Objectifs multiples (V5) — métriques additionnelles pour le classement
  // par objectif (carbone, hiver, location, ROI).
  ges_per_m2_before?: number;   // kgCO2/m²/an (GES)
  ges_per_m2_after?: number;
  ges_per_m2_delta?: number;     // réduction carbone (positif = mieux)
  heating_kwh_ef_before?: number; // kWh éf/an — proxy confort hiver
  heating_kwh_ef_after?: number;
  heating_kwh_ef_delta?: number;  // réduction chauffage (positif = mieux)
  label_after?: string;          // étiquette après travaux (objectif location)
  roi_years?: number | null;     // ROI individuel (positif = rentable)
  // V2 — Économie individuelle (calculée isolément sur le logement) : les
  // colonnes « avec / sans aides » du tableau de synthèse. Attention : les
  // gains ne sont PAS additifs — les totaux d'un bouquet viennent du
  // custom-preview (recalcul combiné moteur).
  cost_min_eur?: number;
  cost_max_eur?: number;
  aids_maprimerenov_eur?: number;
  aids_cee_eur?: number;
  aids_total_eur?: number;
  net_cost_eur?: number;
  annual_eur_saved?: number;
};

export type ApplicableImprovementsResponse = {
  items: Record<string, ImprovementApplicability>;
  base_label?: string;
  base_summer_score?: number;
  base_summer_classe?: string;
  // Métriques de base pour le classement multi-objectifs (V5).
  base_ges_per_m2?: number;
  base_heating_kwh_ef?: number;
  base_primary_per_m2?: number;
  // V4 — Potentiel éolien du département du logement (Atlas ADEME).
  // Utilisé pour avertir l'utilisateur s'il coche "Micro-éolien" dans
  // une zone à faible potentiel (rentabilité quasi nulle).
  wind_potential?: "fort" | "moyen" | "faible";
  department_code?: string | null;
  // V2 — Libellés FR des familles de travaux (segmentation « Mes scénarios »).
  families?: Record<string, string>;
};

/** Objectif de rénovation (V5) — pilote le classement des améliorations. */
export type RenovationObjective =
  | "roi"
  | "carbon"
  | "winter_comfort"
  | "summer_comfort"
  | "rental";

/** Libellé humain d'un objectif (affichage UI). */
export const OBJECTIVE_LABELS: Record<RenovationObjective, string> = {
  roi: "Retour sur investissement financier",
  carbon: "Réduction carbone",
  winter_comfort: "Amélioration du confort hiver",
  summer_comfort: "Amélioration du confort été",
  rental: "Possibilité de location (atteindre l'étiquette E)",
};

export function getApplicableImprovements(logementId: string) {
  return apiFetch<ApplicableImprovementsResponse>(
    `/logements/${logementId}/scenarios/applicable-improvements`,
  );
}

/** Preview du résultat d'un bouquet AVANT simulation persistée. */
export type BouquetPreview = {
  applicable_keys: string[];
  dropped_keys: string[];
  label_before?: string;
  label_after: string | null;
  roi_years: number | null;
  relevance:
    | "excellent" | "bon" | "ok" | "marginal" | "non_rentable" | "inadapte";
  annual_eur_saved: number;
  cost_min_eur: number;
  cost_max_eur: number;
};

export type BouquetsPreviewResponse = {
  items: Record<string, BouquetPreview>;
  current_label?: string;
};

export function getBouquetsPreview(logementId: string) {
  return apiFetch<BouquetsPreviewResponse>(
    `/logements/${logementId}/scenarios/bouquets-preview`,
  );
}

export function createScenario(logementId: string, payload: CreateScenarioPayload) {
  return apiFetch<Scenario>(`/logements/${logementId}/scenarios`, {
    method: "POST",
    body: payload,
  });
}

export function deleteScenario(logementId: string, scenarioId: string) {
  return apiFetch<void>(`/logements/${logementId}/scenarios/${scenarioId}`, {
    method: "DELETE",
  });
}

export function recalculateScenario(logementId: string, scenarioId: string) {
  return apiFetch<Scenario>(
    `/logements/${logementId}/scenarios/${scenarioId}/recalculate`,
    { method: "POST" },
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// V2 — Simulation travaux interactive (tableau de synthèse + sélection)
// ═══════════════════════════════════════════════════════════════════════════

/** Résultat combiné d'une sélection (recalculé par le moteur, jamais sommé). */
export type ScenarioPreview = {
  label_before: string;
  label_after: string;
  final_kwh_saved: number;
  ges_saved: number;
  annual_eur_saved: number;
  cost_min_eur: number;
  cost_max_eur: number;
  aids_total_eur: number;
  net_cost_eur: number;
  roi_years: number | null;
  relevance: string;
  relevance_message: string | null;
  tier?: string | null;
  ampleur?: { is_eligible: boolean; rate_pct: number; mpr_eur: number } | null;
  category?: string;
};

/** Réponse complète du custom-preview (métriques + breakdown + conflits). */
export type CustomPreviewResponse = ScenarioPreview & {
  aids: ScenarioAids & { eligible_eco_ptz: boolean; notes: string[] };
  improvements: string[];
  breakdown: ImprovementBreakdown[];
  dropped_improvements: string[];
  dropped_improvements_reasons: Record<string, string>;
  risk_alerts: {
    severity: "warning" | "info";
    code: string;
    title: string;
    description: string;
  }[];
  engine_version: string;
};

/** Simule une sélection de gestes SANS persister (recalcul combiné moteur). */
export function customPreviewScenario(
  logementId: string,
  improvements: string[],
): Promise<CustomPreviewResponse> {
  return apiFetch<CustomPreviewResponse>(
    `/logements/${logementId}/scenarios/custom-preview`,
    { method: "POST", body: { improvements } },
  );
}

/** Objectif d'optimisation des scénarios recommandés. */
export type OptimalObjective = "roi" | "gain" | "budget" | "climat";

/** Un scénario recommandé (combinaison optimale de gestes, par objectif). */
export type OptimalScenario = {
  objective: OptimalObjective;
  objective_label: string;
  icon: string;
  score_note: string;
  keys: string[];
  label_before: string;
  label_after: string;
  final_kwh_saved: number;
  ges_saved: number;
  annual_eur_saved: number;
  cost_min_eur: number;
  cost_max_eur: number;
  aids_total_eur: number;
  net_cost_eur: number;
  roi_years: number | null;
  relevance: string;
  relevance_message: string | null;
  tier?: string | null;
  ampleur?: { is_eligible: boolean; rate_pct: number; mpr_eur: number } | null;
  category?: string;
};

export type OptimalScenariosResponse = {
  items: Partial<Record<OptimalObjective, OptimalScenario>>;
  current_label: string;
};

/**
 * Scénarios recommandés — 4 combinaisons optimales (une par objectif),
 * calculées par l'optimiseur greedy avec recalcul moteur réel. ~3-6 s au
 * premier appel : à mettre en cache (staleTime 5 min) côté UI.
 */
export function getOptimalScenarios(
  logementId: string,
): Promise<OptimalScenariosResponse> {
  return apiFetch<OptimalScenariosResponse>(
    `/logements/${logementId}/scenarios/optimal`,
  );
}

/** Libellés FR des familles de travaux (segmentation « Mes scénarios »). */
export const FAMILY_LABELS_DEFAULT: Record<string, string> = {
  isolation: "Isolation",
  vitrage: "Vitrage",
  chauffage: "Chauffage",
  ecs: "Eau chaude sanitaire",
  ventilation: "Ventilation",
  enr: "Énergies renouvelables",
  confort_ete: "Confort d'été",
  divers: "Accompagnement",
  mixte: "Mixte",
};
